import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Award } from 'lucide-react';

const DEFAULT_COLORS = { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-warm-border' };

const RARITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  COMMON: DEFAULT_COLORS,
  UNCOMMON: { bg: 'bg-forest-wash', text: 'text-forest', border: 'border-forest/20' },
  RARE: { bg: 'bg-steel/10', text: 'text-steel', border: 'border-steel/20' },
  EPIC: { bg: 'bg-violet/10', text: 'text-violet', border: 'border-violet/20' },
  LEGENDARY: { bg: 'bg-gold/10', text: 'text-gold', border: 'border-gold/20' },
};

interface BadgeToastProps {
  visible: boolean;
  badge: {
    name: string;
    rarity: string;
    xpReward: number;
  };
  onDismiss: () => void;
}

export default function BadgeToast({ visible, badge, onDismiss }: BadgeToastProps) {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const colors = RARITY_COLORS[badge.rarity] ?? DEFAULT_COLORS;

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(onDismiss, 4000);
      return () => clearTimeout(timer);
    }
  }, [visible, onDismiss]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed top-4 inset-x-4 z-50"
          role="alert"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        >
          <button
            onClick={() => {
              onDismiss();
              navigate('/badges');
            }}
            className={`w-full flex items-center gap-3 p-4 rounded-2xl ${colors.bg} border ${colors.border} shadow-lg`}
          >
            <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center`}>
              <Award className={`w-5 h-5 ${colors.text}`} />
            </div>
            <div className="flex-1 text-start min-w-0">
              <p className="text-caption font-medium text-muted-foreground">{t('celebrations.newBadge')}</p>
              <p className={`text-small font-bold ${colors.text} truncate`}>{badge.name}</p>
            </div>
            <span className="text-caption font-bold text-forest bg-forest-wash px-2 py-1 rounded-full whitespace-nowrap">
              +{badge.xpReward} XP
            </span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
